import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppShell } from '../components/AppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FilePdf, ArrowLeft, ChartBar,
  CheckCircle, CalendarBlank, Download,
  Notepad, CalendarCheck, BookOpen
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { downloadBlob } from '../utils/downloadBlob';
import { fetchPdfBlob } from '../utils/fetchPdfBlob';
import { MonthlyPreviewSheet } from '../components/documents/MonthlyPreviewSheet';
import { Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ==================== STATIC FEATURE LISTS ==================== */
const FEATURES_MENSUEL = [
  "Synthèse de tous les comptes rendus du mois",
  "Avancement global actuel et par lot",
  "Retards cumulés avec durée",
  "Total des absences enregistrées",
  "Points bloquants et réserves en cours",
];

const FEATURES_AVANCEMENT = [
  "Synthese avec avancement global",
  "Detail par lot avec tableau des tâches",
  "Alertes retards critiques",
  "Jalons et réserves inclus",
];

const FEATURES_CR = [
  "Avancement global et par lot",
  "Tableau détaillé des tâches par corps d'état",
  "Retards critiques et réserves en cours",
  "Jalons et participants auto-détectés",
  "Zone d'observations manuscrites",
];

const FEATURES_PLANNING = [
  "Tableau complet par lot",
  "Statuts avec codes couleur",
  "Section jalons dédiée",
  "Format paysage A4",
];

/* ==================== DOC CARD ==================== */
function DocCard({ icon: Icon, color, title, badge, badgeColor, description, features, buttonLabel, buttonColor, onGenerate, generating, disabled, chantiers, selectedChantier, onSelectChantier, testId, secondaryAction }) {
  return (
    <Card className={`border-l-4 ${color} hover:shadow-lg transition-all`} data-testid={testId}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className={`w-12 h-12 rounded-xl ${badgeColor}/10 flex items-center justify-center`}>
            <Icon size={24} className={badgeColor.replace('bg-', 'text-')} />
          </div>
          <span className={`text-[10px] font-bold ${badgeColor.replace('bg-', 'text-')} ${badgeColor}/10 px-2 py-1 rounded`}>{badge}</span>
        </div>
        <CardTitle className="text-base mt-2">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-[#4B5563]">{description}</p>
        <ul className="text-xs text-[#6B7280] space-y-1">
          {features.map((f, i) => (
            <li key={`feat-${f.slice(0,20)}-${i}`} className="flex items-center gap-1.5">
              <CheckCircle size={12} className="text-emerald-500 flex-shrink-0" /> {f}
            </li>
          ))}
        </ul>
        {chantiers && (
          <Select value={selectedChantier} onValueChange={onSelectChantier}>
            <SelectTrigger className="h-11" data-testid={`${testId}-chantier-select`}>
              <SelectValue placeholder="Sélectionner un chantier" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              {(chantiers || []).filter(c => c && c.id && c.name).map(c => (
                <SelectItem key={c.id} value={String(c.id)} className="py-3">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button onClick={onGenerate} disabled={disabled || generating}
          className={`w-full h-11 ${buttonColor}`} data-testid={`${testId}-btn`}>
          {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download size={18} className="mr-2" />}
          {generating ? 'Génération en cours...' : buttonLabel}
        </Button>
        {secondaryAction}
      </CardContent>
    </Card>
  );
}

/* ==================== MAIN PAGE ==================== */
export function DocumentsOPCPage() {
  const navigate = useNavigate();
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [crChantier, setCrChantier] = useState('');
  const [crGenerating, setCrGenerating] = useState(false);

  const [raChantier, setRaChantier] = useState('');
  const [raGenerating, setRaGenerating] = useState(false);

  const [planChantier, setPlanChantier] = useState('');
  const [planGenerating, setPlanGenerating] = useState(false);

  const [mensuelChantier, setMensuelChantier] = useState('');
  const [mensuelGenerating, setMensuelGenerating] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchChantiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chantiers?type=opc`, { withCredentials: true });
      setChantiers((res.data || []).filter(c => c && c.id && c.name));
    } catch (_err) { setChantiers([]); }
    finally { setLoading(false); }
  }, [setChantiers, setLoading]);

  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);

  const handleGenerateCR = async () => {
    if (!crChantier) { toast.error('Sélectionnez un chantier'); return; }
    setCrGenerating(true);
    try {
      const res = await fetchPdfBlob('documents/compte-rendu/auto', crChantier);
      downloadBlob(res, 'Compte_Rendu.pdf');
      toast.success('Compte rendu généré avec succes');
    } catch (_err) { toast.error(_err.message || 'Erreur lors de la generation du compte rendu'); }
    finally { setCrGenerating(false); }
  };

  const handleGenerateRA = async () => {
    if (!raChantier) { toast.error('Sélectionnez un chantier'); return; }
    setRaGenerating(true);
    try {
      const res = await fetchPdfBlob('documents/rapport-avancement/auto', raChantier);
      downloadBlob(res, 'Rapport_Avancement.pdf');
      toast.success("Rapport d'avancement généré avec succes");
    } catch (_err) { toast.error(_err.message || "Erreur lors de la génération du rapport"); }
    finally { setRaGenerating(false); }
  };

  const handleGeneratePlanning = async () => {
    if (!planChantier) { toast.error('Sélectionnez un chantier'); return; }
    setPlanGenerating(true);
    try {
      const res = await fetchPdfBlob('tasks/export/pdf', planChantier);
      downloadBlob(res, 'Planning_OPC.pdf');
      toast.success('Planning exporte avec succes');
    } catch (_err) { toast.error(_err.message || "Erreur lors de l'export du planning"); }
    finally { setPlanGenerating(false); }
  };

  const handleGenerateMensuel = async () => {
    if (!mensuelChantier) { toast.error('Sélectionnez un chantier'); return; }
    setMensuelGenerating(true);
    try {
      const res = await fetchPdfBlob('documents/rapport-mensuel/auto', mensuelChantier);
      downloadBlob(res, 'Rapport_Mensuel.pdf');
      toast.success('Rapport mensuel généré avec succes');
    } catch (_err) { toast.error(_err.message || 'Erreur lors de la génération du rapport mensuel'); }
    finally { setMensuelGenerating(false); }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-[#FF6B00]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="documents-opc-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
              onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft size={22} />
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-[#111827] truncate" data-testid="opc-documents-title">Documents OPC</h1>
              <p className="text-sm text-[#4B5563]">Génération automatique — Sélectionnez un chantier et téléchargez</p>
            </div>
          </div>
          <Button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/referentiel-opc/pdf`, { responseType: 'blob', withCredentials: true });
                downloadBlob(res, 'CHANTIX_Referentiel_OPC.pdf');
                toast.success('Référentiel OPC téléchargé');
              } catch (e) {
                toast.error('Erreur lors du téléchargement du référentiel');
              }
            }}
            className="bg-gradient-to-r from-[#0055FF] to-[#3B82F6] text-white hover:opacity-90 shadow-sm flex-shrink-0"
            data-testid="download-referentiel-opc-btn"
          >
            <BookOpen size={18} weight="bold" />
            <span className="ml-2 hidden sm:inline">Référentiel OPC</span>
            <span className="ml-2 sm:hidden">Guide</span>
          </Button>
        </div>

        {/* Reporting Section */}
        <div>
          <h2 className="text-base font-semibold text-[#111827] mb-3" data-testid="section-reporting">Reporting automatique</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <DocCard
              testId="doc-rapport-mensuel"
              icon={CalendarCheck}
              color="border-l-indigo-500"
              title="Rapport Mensuel"
              badge="AUTO"
              badgeColor="bg-indigo-500"
              description="Bilan complet du mois : agrège tous les comptes rendus de chantier, avancement, retards cumulés et total des absences."
              features={FEATURES_MENSUEL}
              buttonLabel="Générer le Rapport Mensuel"
              buttonColor="bg-indigo-600 hover:bg-indigo-700"
              chantiers={chantiers}
              selectedChantier={mensuelChantier}
              onSelectChantier={setMensuelChantier}
              onGenerate={handleGenerateMensuel}
              generating={mensuelGenerating}
              disabled={!mensuelChantier}
              secondaryAction={
                <Button
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!mensuelChantier}
                  className="w-full h-11 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  data-testid="doc-rapport-mensuel-preview-btn"
                >
                  <Eye className="h-4 w-4 mr-2" /> Aperçu avant génération
                </Button>
              }
            />

            <DocCard
              testId="doc-rapport-avancement"
              icon={ChartBar}
              color="border-l-blue-500"
              title="Rapport d'Avancement"
              badge="AUTO"
              badgeColor="bg-blue-500"
              description="Synthese complete du chantier avec % d'avancement, ideal pour le maitre d'ouvrage et les reunions de suivi."
              features={FEATURES_AVANCEMENT}
              buttonLabel="Télécharger le Rapport"
              buttonColor="bg-blue-600 hover:bg-blue-700"
              chantiers={chantiers}
              selectedChantier={raChantier}
              onSelectChantier={setRaChantier}
              onGenerate={handleGenerateRA}
              generating={raGenerating}
              disabled={!raChantier}
            />
          </div>
        </div>

        {/* Documents de chantier Section */}
        <div>
          <h2 className="text-base font-semibold text-[#111827] mb-3" data-testid="section-documents">Documents de chantier</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <DocCard
              testId="doc-compte-rendu"
              icon={Notepad}
              color="border-l-[#FF6B00]"
              title="Compte Rendu de Chantier"
              badge="AUTO"
              badgeColor="bg-[#FF6B00]"
              description="Document de reunion OPC complet, généré automatiquement avec toutes les données du planning et des réserves."
              features={[
                "Avancement global et par lot",
                "Tableau détaillé des tâches par corps d'état",
                "Retards critiques et réserves en cours",
                "Jalons et participants auto-détectés",
                "Zone d'observations manuscrites",
              ]}
              buttonLabel="Télécharger le Compte Rendu"
              buttonColor="bg-[#FF6B00] hover:bg-orange-700"
              chantiers={chantiers}
              selectedChantier={crChantier}
              onSelectChantier={setCrChantier}
              onGenerate={handleGenerateCR}
              generating={crGenerating}
              disabled={!crChantier}
            />

            <DocCard
              testId="doc-planning-export"
              icon={CalendarBlank}
              color="border-l-emerald-500"
              title="Export Planning Gantt"
              badge="AUTO"
              badgeColor="bg-emerald-500"
              description="Export du planning complet en format paysage avec tableau des tâches par lot et section jalons."
              features={FEATURES_PLANNING}
              buttonLabel="Télécharger le Planning"
              buttonColor="bg-emerald-600 hover:bg-emerald-700"
              chantiers={chantiers}
              selectedChantier={planChantier}
              onSelectChantier={setPlanChantier}
              onGenerate={handleGeneratePlanning}
              generating={planGenerating}
              disabled={!planChantier}
            />
          </div>
        </div>
      </div>

      <MonthlyPreviewSheet
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        chantierId={mensuelChantier}
        chantierName={(chantiers.find(c => String(c.id) === String(mensuelChantier)) || {}).name}
      />
    </AppShell>
  );
}
